import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Loader2, Save, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { VoiceToTextTextarea } from "@/components/voice-field";

export default function QuickLeadPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [form, setForm] = useState({
    contactName: "",
    venueName: "",
    phone: "",
    email: "",
    city: "",
    state: "",
    notes: "",
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/leads", {
        contactName: form.contactName || null,
        venueName: form.venueName || null,
        phone: form.phone || null,
        email: form.email || null,
        venueCity: form.city || null,
        venueState: form.state || null,
        notes: form.notes || null,
        source: "Quick capture",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Lead saved" });
      setForm({ contactName: "", venueName: "", phone: "", email: "", city: "", state: "", notes: "" });
    },
    onError: (e: any) =>
      toast({ title: "Failed to save", description: e?.message, variant: "destructive" }),
  });

  function update<K extends keyof typeof form>(k: K, v: string) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  const valid = (form.contactName || form.venueName || form.phone || form.email).length > 0;

  return (
    <div className="flex flex-col h-full pb-20" data-testid="page-quick-lead">
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate("/leads")} data-testid="button-quick-lead-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="text-base font-semibold">Quick capture</div>
          <div className="text-xs text-muted-foreground">Drop a lead in under 30 seconds.</div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-3">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <Label htmlFor="ql-contact">Contact name</Label>
              <Input
                id="ql-contact"
                inputMode="text"
                autoComplete="name"
                value={form.contactName}
                onChange={(e) => update("contactName", e.target.value)}
                data-testid="input-quick-lead-contact"
              />
            </div>
            <div>
              <Label htmlFor="ql-venue">Venue / company</Label>
              <Input
                id="ql-venue"
                value={form.venueName}
                onChange={(e) => update("venueName", e.target.value)}
                data-testid="input-quick-lead-venue"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ql-phone">Phone</Label>
                <Input
                  id="ql-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  data-testid="input-quick-lead-phone"
                />
              </div>
              <div>
                <Label htmlFor="ql-email">Email</Label>
                <Input
                  id="ql-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  data-testid="input-quick-lead-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label htmlFor="ql-city">City</Label>
                <Input id="ql-city" value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="input-quick-lead-city" />
              </div>
              <div>
                <Label htmlFor="ql-state">State</Label>
                <Input id="ql-state" value={form.state} onChange={(e) => update("state", e.target.value)} data-testid="input-quick-lead-state" maxLength={2} />
              </div>
            </div>
            <div>
              <Label htmlFor="ql-notes">Notes</Label>
              <VoiceToTextTextarea
                id="ql-notes"
                rows={4}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Anything to remember…"
                data-testid="input-quick-lead-notes"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-16 left-0 right-0 px-3 pt-2 pb-2 bg-background border-t md:bottom-0">
        <Button
          className="w-full h-12 text-base"
          onClick={() => create.mutate()}
          disabled={!valid || create.isPending}
          data-testid="button-quick-lead-save"
        >
          {create.isPending ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Save className="h-5 w-5 mr-2" />}
          Save lead
        </Button>
      </div>
    </div>
  );
}
